import { ol_format, ol_proj } from "@omnia/exports/openlayers"
import { IDocumentLibraryService, ImportDocument } from "../../documents"
import { IFieldsMap, IFieldsMapFactory } from "../../fields/map"
import { getDefaultMapMarker } from "../../map/styles/markers"
import { DatePicker } from "../../react/datePicker/DatePicker"
import { DialogHeader } from "../../react/DialogHeader"
import { MapView } from "../../react/MapView"
import { showYesNo } from "../../react/msgbox"
import { AngularReactDialog, AngularReactDialogContext } from "../../react/reactAngularDialog"
import { IOmniaSession } from "../../types"
import { convertDatesToJsonUtc } from "../../utils/date"
import { isImageContentType } from "../../utils/utils"
import { ImportDocumentVm } from "../types"

interface Props {
    svc: IDocumentLibraryService
    session: IOmniaSession
    doc: ImportDocumentVm
    manageLibrary?: boolean,
    mapFactory?: IFieldsMapFactory
    close(result?: any): void
}

export const ReactDocumentPropertiesPopup: AngularReactDialog = {

    inject: {
        session: "OmniaSession",
        svc: "OmniaDocumentLibraryService",
        mapFactory: "OmniaFieldsMapFactory"
    },

    component: ({ dependencies, arguments: args, close }: AngularReactDialogContext) => {

        const session: IOmniaSession = dependencies["session"]
        const svc: IDocumentLibraryService = dependencies["svc"]

        const manageLibrary = args["manageLibrary"] as boolean
        const document = args["document"] as ImportDocument
        const mapFactory = args["mapFactory"] as IFieldsMapFactory

        return DocumentPropertiesPopup({
            svc,
            session,
            close,
            manageLibrary,
            mapFactory,
            doc: {
                document,
                selected: false
            }
        })
    }

}


export function DocumentPropertiesPopup(props: Props) {

    const { svc, session, doc, manageLibrary, close, mapFactory } = props

    const [document, setDocument] = React.useState<ImportDocument>(doc.document)
    const [map, setMap] = React.useState<IFieldsMap>(null)
    const [showMap, setShowMap] = React.useState<boolean>(false)

    const readOnly = !manageLibrary

    const years = session.getCroppingYears()

    const isImage = isImageContentType(doc.document.ContentType)

    const imgSrc = svc.getImgPath(doc.document, isImage ? undefined : "thumbnail")

    const originalmgSrc = svc.getImgPath(doc.document, "original")

    const marker = getDefaultMapMarker()

    React.useEffect(() => {

        if (mapFactory && document.ExifLocation && document.ExifLocation.Geometry) {

            const map = mapFactory.createMap({
                viewDefaultExtent: false,
                layers: {
                    fields: {
                        selectable: false,
                    },
                    croppings: {
                        selectable: false,
                    }
                }
            });

            map.enableSelection = false;

            const bing = map.map.basemaps.basemaps.find(b => b.name === 'Bing with labels')

            map.map.basemaps.setBasemap(bing, false);

            var format = new ol_format.WKT();

            var oGeom = document.ExifLocation.Geometry

            var geom = format.readGeometry(oGeom.WellKnownText, {
                dataProjection: "ESPG:4326",
            });

            const timer = window.setTimeout(() => {
                var coords = (geom as any).getCoordinates()
                var coordsTranformed = ol_proj.transform(coords, 'EPSG:4326', 'EPSG:3857')
                let view = map.getView()
                view.setCenter(coordsTranformed)
                view.setResolution(15)
                map.map.setCenter(coordsTranformed)
            }, 100);

            if (geom) {
                timer;
            }

            setMap(map)
            setShowMap(true)

            return () => {
                clearTimeout(timer)
            }
        }

    }, [])

    function remove() {
        const type = isImage ? "picture" : "document"
        showYesNo(`Are you sure you want to remove this ${type} ?`, "Remove warning").then(
            res => {
                if (res) {
                    svc.removeFile(doc.document.ImportDocumentId).then(res => close(true))
                }
            }
        )
    }

    function commit() {
        const d = { ...document, }
        d.ExifDate = convertDatesToJsonUtc(d.ExifDate)
        svc.saveFileProperties(d).then(res => close(true))
    }

    return <div className="omnia-import-document-properties-popup">

        <DialogHeader icon="far fa-file">
            Document properties
        </DialogHeader>

        <div className="modal-body">

            <div className="row">
                <div className="col-md-7 col-content">
                    <a target="_blank" href={originalmgSrc}>
                        <img src={imgSrc} />
                    </a>
                </div>

                <div className="col-md-5">
                    <form className="omnia-form" noValidate autoComplete="off">

                        <table className="omnia-table">
                            <tbody>
                                <tr>
                                    <td className="td-label">Name</td>
                                    <td>
                                        <div className="form-group">
                                            <input className="form-control"
                                                type="text" value={document.FileName} omnia-required="true" disabled={readOnly}
                                                onChange={e => setDocument({ ...document, FileName: e.target.value })}
                                            />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="td-label">Description</td>
                                    <td>
                                        <div className="form-group">
                                            <textarea className="form-control" rows={3} value={document.Description} maxLength={250} disabled={readOnly}
                                                onChange={e => setDocument({ ...document, Description: e.target.value })}
                                            ></textarea>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="td-label">Year</td>
                                    <td>
                                        <div className="form-group">
                                            <select
                                                value={document.Year} className="form-control" required={true} disabled={readOnly}
                                                onChange={e => setDocument({ ...document, Year: parseInt(e.target.value) })}
                                            >
                                                {
                                                    years.map(y => <option key={y.Year} value={y.Year}>{y.Year}</option>)
                                                }
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="td-label">Upload date</td>
                                    <td>
                                        <div className="form-group">
                                            <DatePicker date={document.UploadDate} setDate={v => setDocument({ ...document, UploadDate: v })} disabled={true}>
                                            </DatePicker>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="td-label">Document date</td>
                                    <td>
                                        <div className="form-group">
                                            <DatePicker date={document.ExifDate} setDate={v => setDocument({ ...document, ExifDate: v })}
                                                disabled={readOnly}>
                                            </DatePicker>
                                        </div>
                                    </td>
                                </tr>

                                {document.ExifLocation && map &&
                                    <>
                                        <tr>
                                            <td colSpan={2}>Location</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={2} className="map">
                                                {showMap &&
                                                    <div>
                                                        <MapView map={map} />
                                                        <img src={marker.imgSrc} width={marker.width} height={marker.height} />
                                                    </div>
                                                }
                                            </td>
                                        </tr>
                                    </>
                                }

                            </tbody>
                        </table>

                    </form>

                </div>
            </div>

        </div>

        <div className="modal-footer">

            {
                manageLibrary &&
                <div className="btn-group left">
                    <button type="button" className="btn btn-link btn-danger" onClick={remove}>
                        <i className="icon icon-delete"></i>&nbsp;Delete
                    </button>
                </div>
            }

            {
                readOnly &&
                <div className="btn-group">
                    <button type="button" className="btn btn-info" onClick={e => close()}>Close</button>
                </div>
            }

            {
                (!readOnly) &&
                <>

                    <div className="btn-group">
                        <button
                            type="button"
                            className="btn btn-warning"
                            style={{ marginRight: 5 }}
                            onClick={e => close(false)}>
                            Cancel
                        </button>
                    </div>
                    <div className="btn-group">
                        <button type="submit" className="btn btn-success"
                            disabled={false}
                            onClick={commit}>Save</button>
                    </div>
                </>
            }

        </div>

    </div>

}
